import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Gift,
  Wallet,
  History,
  ArrowUpRight,
  ArrowDownLeft,
  Copy,
  Check,
  Clock,
  Banknote,
  Users,
} from "lucide-react";
import axios from "@/utils/axios";
import { formatCurrency } from "@/utils/format";
import { toast } from "sonner";
import Loader from "@/components/loader/loader";

export default function ReferralDashboard() {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [bankData, setBankData] = useState({
    amount: "",
    bank_name: "",
    account_number: "",
    account_name: "",
  });

  const fetchReferralData = async () => {
    try {
      const [statsRes, historyRes, referralsRes] = await Promise.all([
        axios.get("/referral/stats"),
        axios.get("/referral/history?limit=10"),
        axios.get("/referral/users?limit=10"),
      ]);

      if (statsRes.data.success) setStats(statsRes.data.data);
      if (historyRes.data.success)
        setHistory(historyRes.data.data.transactions);
      if (referralsRes.data.success)
        setReferrals(referralsRes.data.data.referrals);

      // Auto-fill bank details if available
      if (statsRes.data.data) {
        setBankData((prev) => ({
          ...prev,
          bank_name: statsRes.data.data.bank_name || "",
          account_number: statsRes.data.data.account_number || "",
          account_name: statsRes.data.data.account_name || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching referral data:", error);
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferralData();
  }, []);

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    if (parseFloat(bankData.amount) > parseFloat(stats?.balance || 0)) {
      return toast.error("Insufficient balance");
    }

    setWithdrawing(true);
    try {
      await axios.post("/referral/withdraw", bankData);
      toast.success("Withdrawal request submitted successfully!");
      setBankData((prev) => ({ ...prev, amount: "" }));
      fetchReferralData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Withdrawal failed");
    } finally {
      setWithdrawing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex items-center gap-4">
        <Gift className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-primary">Referral Program</h1>
          <p className="text-gray-600">
            Invite friends and earn{" "}
            <span className="text-secondary font-bold font-serif italic text-lg">
              {stats?.referral_percentage || 0}%
            </span>{" "}
            commission on their successful orders.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="md:col-span-1 bg-primary text-white border-none shadow-xl">
          <CardContent className="pt-6">
            <div className="flex flex-col h-full justify-between gap-8">
              <div>
                <p className="text-primary-foreground/80 text-sm font-medium">
                  Available Balance
                </p>
                <h2 className="text-4xl font-black mt-1">
                  {formatCurrency(stats?.balance || 0)}
                </h2>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-primary-foreground/70">
                    Lifetime Earned
                  </span>
                  <span className="font-bold">
                    {formatCurrency(stats?.lifetime_earned || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-primary-foreground/70">
                    Total Withdrawn
                  </span>
                  <span className="font-bold">
                    {formatCurrency(stats?.lifetime_withdrawn || 0)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Banknote className="w-5 h-5 text-secondary" /> Request Withdrawal
            </CardTitle>
            <CardDescription>
              Withdraw your earnings directly to your bank account.
              {stats?.min_withdrawal && (
                <span className="block text-[10px] text-primary mt-1 font-bold">
                  Limits: {formatCurrency(stats.min_withdrawal)} -{" "}
                  {formatCurrency(stats.max_withdrawal)}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleWithdrawal} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount text-xs">Amount to Withdraw</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    required
                    min={stats?.min_withdrawal || 0}
                    max={stats?.max_withdrawal || 1000000}
                    value={bankData.amount}
                    onChange={(e) =>
                      setBankData({ ...bankData, amount: e.target.value })
                    }
                  />
                  <p className="text-[10px] text-gray-400">
                    Min: {formatCurrency(stats?.min_withdrawal || 0)}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_name text-xs">Bank Name</Label>
                  <Input
                    placeholder="e.g. GTBank, Zenith"
                    required
                    value={bankData.bank_name}
                    onChange={(e) =>
                      setBankData({ ...bankData, bank_name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account_number text-xs">Account Number</Label>
                  <Input
                    placeholder="10 digits"
                    required
                    value={bankData.account_number}
                    onChange={(e) =>
                      setBankData({
                        ...bankData,
                        account_number: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account_name text-xs">Account Name</Label>
                  <Input
                    placeholder="As it appears on bank"
                    required
                    value={bankData.account_name}
                    onChange={(e) =>
                      setBankData({ ...bankData, account_name: e.target.value })
                    }
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-secondary hover:bg-secondary/90 text-white"
                disabled={
                  withdrawing ||
                  !stats?.balance ||
                  parseFloat(stats.balance) <= 0
                }
              >
                {withdrawing ? "Processing..." : "Submit Payout Request"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-secondary" /> My Referrals
            </CardTitle>
            <CardDescription>
              A list of people who signed up using your code.
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-secondary border-secondary">
            Total: {stats?.referral_count || 0}
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th className="px-6 py-3 font-bold">User Name</th>
                  <th className="px-6 py-3 font-bold">Joined Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {referrals.map((ref) => (
                  <tr
                    key={ref.id}
                    className="bg-white hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 font-medium text-primary">
                      {ref.first_name} {ref.last_name}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {new Date(ref.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {referrals.length === 0 && (
                  <tr>
                    <td
                      colSpan="2"
                      className="px-6 py-10 text-center text-gray-400 italic"
                    >
                      You haven't referred anyone yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-primary" /> Transaction History
            </CardTitle>
            <CardDescription>
              A log of your referral earnings and withdrawals.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th className="px-6 py-3 font-bold">Details</th>
                  <th className="px-6 py-3 font-bold">Type</th>
                  <th className="px-6 py-3 font-bold">Amount</th>
                  <th className="px-6 py-3 font-bold">Status</th>
                  <th className="px-6 py-3 font-bold">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map((tx) => (
                  <tr
                    key={tx.id}
                    className="bg-white hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <p className="font-medium text-primary">
                        {tx.description}
                      </p>
                      {tx.reference_id && (
                        <p className="text-xs text-gray-400">
                          Ref: {tx.reference_id}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 capitalize">
                        {tx.transaction_type === "earned" ? (
                          <ArrowDownLeft className="w-4 h-4 text-green-500" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-blue-500" />
                        )}
                        {tx.transaction_type}
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 font-bold ${tx.transaction_type === "earned" ? "text-green-600" : "text-blue-600"}`}
                    >
                      {tx.transaction_type === "earned" ? "+" : "-"}
                      {formatCurrency(tx.amount)}
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={getStatusColor(tx.status)}>
                        {tx.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-10 text-center text-gray-400 italic"
                    >
                      No transactions found yet. Start referring to earn!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
